<template>
  <div class="tracking-page">
    <tracking-page-header
      title="Where people drop off"
      subtitle="Same steps patients see: Widget → Checkout → Confirmation → Complete your profile → Additional details."
      :from-date="fromDate"
      :to-date="toDate"
      :loading="loading"
      :chat-filters="chatFilters"
      :page-summary="pageSummary"
      @apply="fromDate = $event.fromDate; toDate = $event.toDate; loadFunnel()"
      @refresh="loadFunnel"
    >
      <template #actions>
        <div class="trk-segment" title="Signed-in patients skip Complete your profile">
          <button type="button" class="trk-segment-btn" :class="{ active: userType === '' }" @click="setUserType('')">All</button>
          <button type="button" class="trk-segment-btn" :class="{ active: userType === 'GUEST' }" @click="setUserType('GUEST')">New patients</button>
          <button type="button" class="trk-segment-btn" :class="{ active: userType === 'EXISTING' }" @click="setUserType('EXISTING')">Signed in</button>
        </div>
        <button class="btn trk-btn-ghost" type="button" @click="exportCsv"><i class="fa fa-download"></i> Export</button>
      </template>
    </tracking-page-header>

    <div class="trk-kpi-grid">
      <div class="trk-kpi">
        <div class="trk-kpi-label">Overall success</div>
        <div class="trk-kpi-value">{{ formatPct(funnel.bookingConversionPercent) }}</div>
        <div class="trk-kpi-help">People who booked after starting</div>
      </div>
      <div class="trk-kpi">
        <div class="trk-kpi-label">Biggest drop-off</div>
        <div class="trk-kpi-value" style="font-size:1.2rem">{{ biggestDropOff }}</div>
        <div class="trk-kpi-help">Step losing the most people</div>
      </div>
      <div class="trk-kpi">
        <div class="trk-kpi-label">Left without finishing</div>
        <div class="trk-kpi-value">{{ formatNumber(funnel.abandoned) }}</div>
        <div class="trk-kpi-help">Abandoned journeys</div>
      </div>
    </div>

    <div class="trk-panel">
      <div class="trk-panel-head">
        <div>
          <h2>Registration journey</h2>
          <p>Each bar shows how many people reached that step. Click a bar to open matching journeys.</p>
        </div>
      </div>
      <div class="trk-panel-body">
        <div class="trk-funnel-list">
          <button
            type="button"
            class="trk-funnel-row trk-funnel-row--click"
            v-for="(step, index) in visualSteps"
            :key="step.step"
            @click="openSessionsForStep(step)"
          >
            <div>
              <div class="trk-funnel-label">{{ index + 1 }}. {{ step.plainLabel }}</div>
              <small class="text-muted" v-if="step.dropOff">Lost {{ formatNumber(step.dropOff) }} from previous step</small>
              <small class="text-muted d-block" v-if="step.step === 'PROFILE' && !userType">New patients only — signed-in patients skip “Complete your profile”</small>
            </div>
            <div class="trk-funnel-bar">
              <div class="trk-funnel-fill" :style="{ width: step.width + '%' }"></div>
            </div>
            <div class="trk-funnel-count">{{ formatNumber(step.users) }}</div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TrackingPageHeader from './TrackingPageHeader.vue';
import { defaultDateRange, formatNumber, formatPct, rangeParams } from './trackingShared';

export default {
  name: 'TrackingFunnel',
  components: { TrackingPageHeader },
  data() {
    const range = defaultDateRange(7);
    return {
      fromDate: range.fromDate,
      toDate: range.toDate,
      userType: '',
      funnel: { steps: [], bookingConversionPercent: 0, abandoned: 0 },
      loading: false
    };
  },
  computed: {
    visualSteps() {
      const steps = this.funnel.steps || [];
      const max = Math.max(1, ...steps.map((s) => Number(s.users || 0)));
      const plain = {
        WIDGET: 'Widget',
        CHECKOUT: 'Checkout',
        CONFIRMATION: 'Confirmation',
        PROFILE: 'Complete your profile',
        ADDITIONAL_DETAILS: 'Additional details',
        FINISHED: 'Finished all steps',
        ENTRY: 'Checkout',
        DETAILS: 'Checkout',
        ASSESSMENT: 'Checkout',
        ACCOUNT: 'Checkout',
        PAYMENT: 'Checkout',
        BOOKING: 'Checkout'
      };
      return steps.map((step) => ({
        ...step,
        plainLabel: plain[step.step] || step.label,
        width: Math.max(4, Math.round((Number(step.users || 0) * 100) / max))
      }));
    },
    biggestDropOff() {
      const steps = this.funnel.steps || [];
      let best = null;
      steps.forEach((s) => {
        if (s.dropOff == null) return;
        if (!best || s.dropOff > best.dropOff) best = s;
      });
      if (!best) return 'None';
      return `${best.label} (${formatNumber(best.dropOff)})`;
    },
    chatFilters() {
      return { ...rangeParams(this.fromDate, this.toDate), userType: this.userType || undefined };
    },
    pageSummary() {
      return `${formatPct(this.funnel.bookingConversionPercent)} booking conversion; biggest drop-off: ${this.biggestDropOff}; ${formatNumber(this.funnel.abandoned)} abandoned`;
    }
  },
  created() { this.loadFunnel(); },
  methods: {
    formatNumber,
    formatPct,
    setUserType(value) {
      this.userType = value;
      this.loadFunnel();
    },
    loadFunnel() {
      this.loading = true;
      const params = rangeParams(this.fromDate, this.toDate);
      if (this.userType) params.userType = this.userType;
      this.$onboardingTrackingService.getFunnel(params)
        .then((res) => { this.funnel = (res && res.data) || this.funnel; })
        .finally(() => { this.loading = false; });
    },
    openSessionsForStep(step) {
      const query = {
        from: this.fromDate,
        to: this.toDate
      };
      const map = {
        CHECKOUT: '',
        CONFIRMATION: 'CONFIRMATION',
        PROFILE: 'PROFILE',
        ADDITIONAL_DETAILS: 'ADDITIONAL_DETAILS',
        FINISHED: 'ADDITIONAL_DETAILS'
      };
      if (map[step.step]) query.step = map[step.step];
      if (step.step === 'FINISHED') query.status = 'COMPLETED';
      else if (step.dropOff > 0 && step.step !== 'CHECKOUT') query.status = 'ABANDONED';
      this.$router.push({ path: '/tracking/sessions', query });
    },
    exportCsv() {
      const params = rangeParams(this.fromDate, this.toDate);
      if (this.userType) params.userType = this.userType;
      this.$onboardingTrackingService.exportSessions(params);
    }
  }
};
</script>

<style scoped>
.trk-segment {
  display: inline-flex;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-right: 0.5rem;
}
.trk-segment-btn {
  border: 0;
  background: #fff;
  padding: 0.35rem 0.7rem;
  font-size: 0.82rem;
  color: #475569;
  cursor: pointer;
}
.trk-segment-btn.active {
  background: #0f766e;
  color: #fff;
}
.trk-funnel-row--click {
  width: 100%;
  text-align: left;
  background: transparent;
  border: 0;
  padding: 0;
  display: grid;
  grid-template-columns: minmax(140px, 1.2fr) 1fr auto;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 0.75rem;
  cursor: pointer;
}
.trk-funnel-row--click:hover .trk-funnel-label {
  color: #0f766e;
}
</style>
